import React from 'react'
import Dialog from '../ui/dialog'
import Button from '../ui/button'
import { ArrowDownRight, ArrowUpRight, Info, Lock } from 'lucide-react'
import type { PreviewPlanChangeResponse } from '../../api/billing'

export interface PlanChangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPlanName: string
  targetPlanName: string
  preview: PreviewPlanChangeResponse | null
  loading: boolean
  onConfirm: () => void | Promise<void>
}

const PlanChangeDialog: React.FC<PlanChangeDialogProps> = ({
  open,
  onOpenChange,
  currentPlanName,
  targetPlanName,
  preview,
  loading,
  onConfirm,
}) => {
  const isUpgrade = preview?.isUpgrade ?? true
  const title = isUpgrade ? 'Confirm upgrade' : 'Confirm downgrade'
  const description = isUpgrade
    ? `You're moving from ${currentPlanName} to ${targetPlanName}.`
    : `You're moving from ${currentPlanName} to ${targetPlanName}.`

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} description={description} size="sm" heightMode="auto">
      <div className="space-y-5">
        {/* Summary card */}
        <div className="rounded-xl border border-black/10 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-foreground/70">
              <div className="flex items-center gap-2">
                {isUpgrade ? (
                  <ArrowUpRight className="h-4 w-4 text-brand-600" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-foreground/60" />
                )}
                <span>
                  {currentPlanName} 
                  <span className="text-foreground/40">→</span> {targetPlanName}
                </span>
              </div>
              {preview?.description && (
                <p className="mt-2 text-xs text-foreground/60">{preview.description}</p>
              )}
            </div>
            {preview && (
              <div className="text-right">
                {isUpgrade ? (
                  <>
                    <div className="text-sm font-medium text-foreground">Due today</div>
                    <div className="text-base text-foreground/80">{preview.currency} {preview.monetaryAmount.toFixed(2)}</div>
                  </>
                ) : (
                  <div className="text-xs text-foreground/60">Takes effect next cycle</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fine print / trust */}
        <div className="flex items-center justify-between text-[11px] text-foreground/60">
          <span className="inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Secure billing</span>
          <span className="inline-flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Cancel anytime</span>
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" className="flex-1" onClick={() => onOpenChange(false)} disabled={loading}>
            Not now
          </Button>
          <Button className="flex-1" onClick={onConfirm} disabled={loading || !preview}>
            {loading ? 'Processing…' : isUpgrade ? 'Upgrade now' : 'Confirm downgrade'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default PlanChangeDialog
